import { useMutation, useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useMemo } from 'react';

import { SupplierContactData } from '../components/SupplierContactEditForm/SupplierContactEditForm.schema.ts';
import { SupplierContactDetailsQueryVariables } from '@/generated/gql/graphql.ts';
import { HASURA_ROLE_HEADER } from '~/common/constants';
import { RolesType } from '~/common/types';
import { useAuthRoles } from '~/hooks/useAuthRoles/useAuthRoles.ts';

import { SUPPLIER_CONTACT_DETAILS_UPDATE_MUTATION } from './SupplierContactDetailsMutation.gql.ts';
import { SUPPLIER_CONTACT_DETAILS_QUERY } from './SupplierContactDetailsQuery.gql.ts';
import { fromSupplierContactFormData, toSupplierContactFormData } from './useSupplierContactEditBackend.util.ts';

export interface UseSupplierContactEditBackendProps {
  contactId?: string;
}

export interface UseSupplierContactEditBackendResult {
  data: SupplierContactData | null;
  loading: boolean;
  saving: boolean;
  save: (data: SupplierContactData) => Promise<boolean>;
}

export const useSupplierContactEditBackend = ({
  contactId,
}: UseSupplierContactEditBackendProps): UseSupplierContactEditBackendResult => {
  const { hasAnyRole } = useAuthRoles();
  const { onError } = useCustomSnackbar();

  const variables: SupplierContactDetailsQueryVariables = {
    id: contactId,
  };

  const context = useMemo(() => {
    return {
      headers: {
        [HASURA_ROLE_HEADER]: hasAnyRole([RolesType.Requester, RolesType.BuyerAdmin, RolesType.Admin]),
      },
    };
  }, [hasAnyRole]);

  const { data, loading } = useQuery(SUPPLIER_CONTACT_DETAILS_QUERY, {
    variables,
    fetchPolicy: 'network-only',
    skip: !variables.id,
    context,
  });

  const [update, { loading: saving }] = useMutation(SUPPLIER_CONTACT_DETAILS_UPDATE_MUTATION, {
    context,
    onError,
  });

  const contactData = data?.cmdi.contact[0];

  return {
    data: useMemo(() => toSupplierContactFormData(contactData), [contactData]),
    loading,
    saving,
    save: async (updatedData) => {
      const { errors } = await update({
        variables: {
          input: fromSupplierContactFormData(updatedData),
        },
      });
      return !errors || errors.length === 0;
    },
  };
};
